import { useSignIn, useUser } from "@clerk/clerk-react";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";

type Step = "request" | "verify" | "oauth_only";

const GENERIC_REQUEST_MESSAGE = "If an account exists for this email, we've sent you a code.";

export default function ForgotPasswordPage() {
  const { signIn, setActive, isLoaded } = useSignIn();
  const { isSignedIn, isLoaded: userLoaded } = useUser();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("request");
  const [submitting, setSubmitting] = useState(false);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [requestMessage, setRequestMessage] = useState<string>("");
  const [verifyError, setVerifyError] = useState<string>("");
  const [resending, setResending] = useState(false);
  const [resentMessage, setResentMessage] = useState<string>("");

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally trigger on code change
  useEffect(() => {
    // Clear the transient "code resent" hint when the user starts typing again.
    setResentMessage("");
  }, [code]);

  if (userLoaded && isSignedIn) {
    return <Navigate to="/" replace />;
  }

  const isOAuthOnlyError = (err: unknown): boolean => {
    // Clerk surfaces this either as a missing_factor error code or a message
    // mentioning "password". Be permissive — false positives just send the user
    // back to /sign-in, which is a safe fallback.
    if (!err || typeof err !== "object") return false;
    const errors = (err as { errors?: Array<{ code?: string; message?: string }> }).errors;
    if (!errors) return false;
    return errors.some(
      (e) =>
        (e.code === "form_password_incorrect") === false &&
        ((e.code ?? "").includes("password") ||
          (e.message ?? "").toLowerCase().includes("password"))
    );
  };

  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signIn) return;
    setSubmitting(true);
    setRequestMessage("");
    try {
      await signIn.create({
        strategy: "reset_password_email_code",
        identifier: email,
      });
    } catch (err) {
      // Swallow — we always show the same message to avoid email enumeration.
      // Logged for debugging only.
      console.warn("reset_password_email_code request failed:", err);
    } finally {
      setRequestMessage(GENERIC_REQUEST_MESSAGE);
      setStep("verify");
      setSubmitting(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signIn || !setActive) return;
    setSubmitting(true);
    setVerifyError("");
    try {
      const result = await signIn.attemptFirstFactor({
        strategy: "reset_password_email_code",
        code,
        password,
      });
      if (result.status === "complete" && result.createdSessionId) {
        await setActive({ session: result.createdSessionId });
        navigate("/", { replace: true });
        return;
      }
      setVerifyError("We couldn't reset your password. Please try again.");
    } catch (err: unknown) {
      if (isOAuthOnlyError(err)) {
        setStep("oauth_only");
        return;
      }
      const message =
        err instanceof Error ? err.message : "The code didn't work. Please try again.";
      setVerifyError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!signIn || !email) return;
    setResending(true);
    setResentMessage("");
    try {
      await signIn.create({
        strategy: "reset_password_email_code",
        identifier: email,
      });
    } catch (err) {
      console.warn("reset_password_email_code resend failed:", err);
    } finally {
      setResentMessage("A new code has been sent if the email exists.");
      setResending(false);
    }
  };

  const busy = submitting || !isLoaded;

  return (
    <div className="min-h-screen bg-gradient-to-br from-sidebar via-sidebar to-emerald-900 flex items-center justify-center p-4">
      <div className="bg-white/10 backdrop-blur-md p-12 rounded-2xl shadow-2xl max-w-md w-full border border-white/20">
        <div className="flex flex-col items-center text-center">
          <div className="mb-6">
            <img src="/dfr-logo.svg" alt="DFR Logo" className="h-12 w-auto" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Reset your password</h1>

          {step === "request" && (
            <>
              <p className="text-white/60 mb-8">
                Enter your email and we'll send you a code to reset your password.
              </p>
              <form onSubmit={handleRequest} className="w-full space-y-3">
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  autoFocus
                  className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-white/40"
                />
                <button
                  type="submit"
                  disabled={busy}
                  className="w-full bg-white text-sidebar px-6 py-4 rounded-xl font-bold hover:bg-white/90 disabled:opacity-70 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  Send code
                </button>
              </form>
            </>
          )}

          {step === "verify" && (
            <>
              <p className="text-white/60 mb-2">{requestMessage}</p>
              <p className="text-white/40 text-sm mb-6">
                Enter the code and choose a new password.
              </p>
              <form onSubmit={handleVerify} className="w-full space-y-3">
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="Verification code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  autoFocus
                  className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-white/40 tracking-widest text-center"
                />
                <input
                  type="password"
                  placeholder="New password (min 8 characters)"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-white/40"
                />
                {verifyError && <p className="text-red-400 text-sm text-left">{verifyError}</p>}
                {resentMessage && (
                  <p className="text-emerald-300 text-sm text-left">{resentMessage}</p>
                )}
                <button
                  type="submit"
                  disabled={busy}
                  className="w-full bg-white text-sidebar px-6 py-4 rounded-xl font-bold hover:bg-white/90 disabled:opacity-70 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  Reset password
                </button>
              </form>
              <button
                type="button"
                onClick={handleResend}
                disabled={resending || submitting}
                className="mt-4 text-white/60 hover:text-white text-sm transition-colors disabled:opacity-50"
              >
                {resending ? "Resending..." : "Resend code"}
              </button>
            </>
          )}

          {step === "oauth_only" && (
            <>
              <p className="text-white/60 mb-8">
                This account uses Google sign-in. There's no password to reset — continue with
                Google instead.
              </p>
              <Link
                to="/sign-in"
                className="w-full bg-white text-sidebar px-6 py-4 rounded-xl font-bold hover:bg-white/90 transition-colors inline-block"
              >
                Back to sign in
              </Link>
            </>
          )}

          <div className="mt-8 pt-6 border-t border-white/10 w-full text-center">
            <Link
              to="/sign-in"
              className="text-white/60 hover:text-white text-sm transition-colors"
            >
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
